import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import { AuthContext } from "../../Context/AuthContext";
import { Modal, Button, Table } from "react-bootstrap";
import Preloader from "../../shared/PreLoaders";
import { toast } from "react-toastify";
import "./Cashbook.css";

const ClosingBalance = ({ open, onClose }) => {
  const { selectedJfmcType } = useContext(AuthContext);
  const [vfcData, setVfcData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const Api = process.env.REACT_APP_API;
  const today = new Date();
  // Previous month
  const previousMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1
  );
  const [selectedMonth, setSelectedMonth] = useState(
    `${previousMonth.getFullYear()}-${String(
      previousMonth.getMonth() + 1
    ).padStart(2, "0")}`
  );
  const maxMonth = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}`;
  const [selectedYear, selectedMon] = selectedMonth
    .split("-")
    .map(Number);

  // Last day of selected month
  const lastDay = new Date(selectedYear, selectedMon, 0);
  const closingDate = `${selectedYear}-${String(selectedMon).padStart(
    2,
    "0"
  )}-${String(lastDay.getDate()).padStart(2, "0")}`;
  // Execution date = today
  const executionDate = new Date().toISOString().split("T")[0];;
  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    if (!selectedJfmcType) return;

    const fetchVfc = async () => {
      setLoading(true);
      try {
        const config = Authorization();

        const vfcRes = await axios.post(
          `${Api}/api/vfcs/names`,
          { jfmcTypeId: selectedJfmcType._id },
          config
        );

        const balanceRes = await axios.post(
          `${Api}/api/closing-balance/march`,
          {
            jfmcTypeId: selectedJfmcType._id,
            month: selectedMonth,      // "2025-07"
          },
          config
        );

        const balances = balanceRes.data.balances || [];

        const updated = (vfcRes.data.vfcs || []).map((vfc) => {
          const existing = balances.find(
            (b) => b.vfcId.toString() === vfc._id.toString()
          );

          return {
            ...vfc,
            cash: existing ? existing.cash : "",
            bank: existing ? existing.bank : "",
            isExisting: !!existing,
            balanceId: existing?._id,
            isEditing: false,
          };
        });

        setVfcData(updated);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchVfc();
  }, [selectedJfmcType, selectedMonth]);

  /* ---------------- CHECK IF ANY DATA EXISTS ---------------- */
  const hasExistingData = vfcData.some((v) => v.isExisting);

  /* ---------------- HANDLE EDIT ---------------- */
  const handleEdit = (index) => {
    const updated = [...vfcData];
    updated[index].isEditing = true;
    setVfcData(updated);
  };

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (index, field, value) => {
    const updated = [...vfcData];

    let newValue = value === "" ? "" : Number(value);

    updated[index][field] = newValue;
    setVfcData(updated);
  };

  /* ---------------- HANDLE SUBMIT ---------------- */
  const handleSubmit = async () => {
    // Prevent adding balance for current month
    const currentMonth = new Date().toISOString().slice(0, 7); // e.g. "2026-08"

    if (selectedMonth === currentMonth) {
      toast.error("You cannot add bank balance for the current month.");
      return;
    }

    setSubmitting(true);

    try {
      const config = Authorization();

      const filteredData = vfcData.filter(
        (v) => v.cash !== "" || v.bank !== ""
      );

      if (filteredData.length === 0) {
        toast.error("Please enter at least one value");
        return;
      }

      const promises = filteredData.map((v) => {
        const payload = {
          vfcId: v._id,
          month: selectedMonth,
          cash: Number(v.cash || 0),
          bank: Number(v.bank || 0),
        };

        if (v.isExisting) {
          const confirmUpdate = window.confirm(
            `Are you sure you want to update ${v.vfcName}?`
          );

          if (!confirmUpdate) return Promise.resolve();

          return axios.put(
            `${Api}/api/closing-balance/${v.balanceId}`,
            payload,
            config
          );
        }

        return axios.post(
          `${Api}/api/closing-balance`,
          payload,
          config
        );
      });

      await Promise.all(promises);

      toast.success("Closing balances saved!");
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      dialogClassName="closing-modal-compact"
    >
      {/* HEADER */}
      <Modal.Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 15px",
          background: "#2e7d32",
          color: "#fff",
        }}
      >
        <Modal.Title style={{ margin: 0, fontSize: "1rem" }}>
          Update Bank Balance
        </Modal.Title>

        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "transparent",
            color: "#fff",
            fontSize: "18px",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </Modal.Header>

      {/* BODY */}
      <Modal.Body>
        {loading && <Preloader loading />}

        {!selectedJfmcType && (
          <div className="text-center text-danger">
            Please select JFMC Type
          </div>
        )}

        {selectedJfmcType && (
          <><div className="mb-3">
            <label className="form-label fw-bold">
              Select Month
            </label>

            <input
              type="month"
              className="form-control"
              value={selectedMonth}
              max={maxMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
            <div 
              style={{
                marginBottom: 10,
                fontWeight: "600",
                fontSize: "1rem",
                background: "#e9f7ef",
                padding: "6px 10px",
                borderRadius: "6px",
              }}
            >
              Update the Bank Balance For {selectedMonth}
            </div>

            <Table bordered hover className="mb-0 text-center">
              <thead style={{ background: "#f1f3f5" }}>
                <tr>
                  <th style={{ width: "40px" }}>#</th>
                  <th style={{ width: "30%" }}>VFC Name</th>
                  {/* <th style={{ width: "100px" }}>Cash</th> */}
                  <th style={{ width: "100px" }}>Bank Balance</th>

                  {/* 🔥 Show only if any data exists */}
                  {hasExistingData && (
                    <th style={{ width: "80px" }}>Action</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {vfcData.map((vfc, index) => (
                  <tr
                    key={vfc._id}
                    style={{
                      background: vfc.isEditing ? "#fff3cd" : "white",
                    }}
                  >
                    <td>{index + 1}</td>

                    <td style={{ textAlign: "left" }}>
                      {vfc.vfcName}
                      {vfc.isExisting && (
                        <span
                          style={{
                            color: "green",
                            marginLeft: 6,
                            fontSize: "12px",
                          }}
                        >
                          (Saved)
                        </span>
                      )}
                    </td>

                    {/* <td>
                      <input
                        type="number"
                        value={vfc.cash === 0 ? "" : vfc.cash}
                        min="0"
                        disabled={
                          hasExistingData &&
                          vfc.isExisting &&
                          !vfc.isEditing
                        }
                        onChange={(e) =>
                          handleChange(index, "cash", e.target.value)
                        }
                        className="form-control input-compact"
                      />
                    </td> */}

                    <td>
                      <input
                        type="number"
                        value={vfc.bank === 0 ? "" : vfc.bank}
                        min="0"
                        disabled={
                          hasExistingData &&
                          vfc.isExisting &&
                          !vfc.isEditing
                        }
                        onChange={(e) =>
                          handleChange(index, "bank", e.target.value)
                        }
                        className="form-control input-compact"
                      />
                    </td>

                    {/* 🔥 Action column */}
                    {hasExistingData && (
                      <td>
                        {vfc.isExisting && !vfc.isEditing ? (
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEdit(index)}
                          >
                            Edit
                          </button>
                        ) : vfc.isExisting ? (
                          <span
                            style={{
                              color: "green",
                              fontSize: "12px",
                            }}
                          >
                            Editable
                          </span>
                        ) : (
                          <span
                            style={{
                              color: "#999",
                              fontSize: "12px",
                            }}
                          >
                            New
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="mt-3"
            >
              {submitting ? "Saving..." : "Save All"}
            </Button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ClosingBalance;